/**
 * "Save the Admiral" campaign map screen — the illustrated region map.
 *
 * A hand-drawn (inline-SVG) sea chart: three Regions, each a cluster of three
 * state islands joined by dotted shipping lanes (the manual's mutually-adjacent
 * political cluster). Each island shows its initials and a status — a swayed
 * state glows green with "++", a "swayed against" state (resistance 8+) shows a
 * red X and "--", a neutral state shows its resistance number, and a locked
 * Region's islands are greyed with a padlock until the prior Region is won.
 * (docs/reference/site/manual/sta-map.md.)
 *
 * Clicking a playable island opens a battle-options card (resistance, win
 * ratio, both budgets, the reward offered) with "Begin battle"; that calls
 * `onStart(region, state)`. All campaign arithmetic lives in rules/campaign.ts;
 * this file is purely presentation + input. Deterministic (no RNG) so the
 * campaign-map golden is stable.
 */
import {
  CAMP_REGIONS, STATES_PER_REGION, TOTAL_STATES, battleConfig, campaignComplete,
  isAgainst, isSwayed, regionUnlocked, renownBudget, stateResistance, swayedCount,
  type Campaign,
} from '../rules/campaign';
import { SCREEN_MAX_W, SCREEN_MAX_H } from './screen';
import { showSettings, type SettingsHandle } from './settings';
import { ICON_GEAR, ICON_VP } from './icons';

const CSS = `
.ar-campaign { --navy:hsl(217, 53.7%, 16.1%); --navy2:hsl(220, 60%, 11.8%); --ink:hsl(219, 58.3%, 9.4%); --parch:hsl(44, 53.7%, 83.9%); --parch2:hsl(40, 46.9%, 71.2%);
  --wood:hsl(31, 47.6%, 28.4%); --wood-d:hsl(30, 54.2%, 18.8%); --brass:hsl(43, 63.2%, 58.4%); --brass-l:hsl(46, 80.7%, 73.5%); --brass-d:hsl(42, 61.6%, 34.7%);
  --txt:hsl(28, 55.6%, 10.6%); --head:hsl(23, 66.7%, 21.2%); --ok:hsl(105, 41.7%, 29.6%); --redx:hsl(7, 70.6%, 41.4%);
  /* fill the viewport like the game screen: a fixed flex column, capped + centred,
     with the map filling the leftover height (header + info bar are fixed-height). */
  position: fixed; inset: 0; max-width: ${SCREEN_MAX_W}px; max-height: ${SCREEN_MAX_H}px; margin: auto;
  display: flex; flex-direction: column;
  font-family: Georgia, 'Times New Roman', serif; color: var(--txt); user-select: none; }
/* Header chrome mirrors the in-game / buy-screen bar (.ar-top): same navy
   gradient, ink border, brass base-rule, serif brass lettering. HUD stats on the
   left; screen name + settings gear on the right. */
.ar-camp-top { flex: 0 0 auto; display: flex; flex-wrap: wrap; align-items: stretch; background: linear-gradient(180deg, hsl(219, 52.2%, 22.2%), var(--navy) 60%, var(--navy2));
  border: 2px solid var(--ink); border-bottom: 3px solid var(--brass-d); }
/* "Swayed" progress dial — the EXACT turn-block treatment from the game bar: a
   clockwise-filling pie beside an uppercase cap over a "done / total" count. The
   fill is driven by the registered --swp custom property (a 0–100 number). */
@property --swp { syntax: '<number>'; initial-value: 0; inherits: false; }
.ar-camp-top .ar-turn { flex: 0 0 auto; display: flex; flex-direction: row; align-items: center; gap: 10px; padding: 6px 16px; }
.ar-camp-top .ar-turn-pie { position: relative; box-sizing: border-box; width: 26px; height: 26px; border-radius: 50%;
  flex: 0 0 auto; overflow: hidden; border: 2px solid var(--brass-d);
  background: hsla(0, 0%, 0%, 0.5); box-shadow: 0 1px 2px hsla(0, 0%, 0%, 0.5); }
.ar-camp-top .ar-turn-pie-fill { position: absolute; inset: 0; transition: --swp 0.45s ease;
  background: conic-gradient(var(--brass-l) calc(var(--swp, 0) * 1%), transparent 0); }
.ar-camp-top .ar-turn-text { display: flex; flex-direction: column; line-height: 0.95; }
.ar-camp-top .ar-turn-cap { color: var(--brass-l); font-weight: 700; font-size: 11px; letter-spacing: 1px;
  text-transform: uppercase; text-shadow: 0 1px 0 hsl(0, 0%, 0%); }
.ar-camp-top .ar-turn-count { color: var(--brass-l); font-weight: 700; text-shadow: 0 1px 0 hsl(0, 0%, 0%); white-space: nowrap; }
.ar-camp-top .ar-turn-count b { font-size: 14px; }
.ar-camp-top .ar-turn-max { opacity: 0.7; font-size: 11px; margin-left: 2px; }
/* the points stat (fleet budget = base + renown) sits to the right of the dial */
.ar-camp-top .stat { flex: 0 0 auto; display: flex; flex-direction: row; align-items: center; gap: 6px;
  padding: 0 16px; border-left: 1px solid hsla(0, 0%, 100%, 0.08); white-space: nowrap; }
.ar-camp-top .stat .val { display: inline-flex; align-items: center; gap: 2px;
  color: var(--brass-l); font-weight: 700; font-size: 15px; font-variant-numeric: tabular-nums;
  text-shadow: 0 1px 0 hsl(0, 0%, 0%); }
.ar-camp-top .stat .val svg { width: 1.15em; height: 1.15em; display: block; }
/* the screen name (styled like the in-game mode heading) — margin-left:auto pushes
   it and the gear to the right, HUD stats staying on the left. */
.ar-camp-top .title { margin-left: auto; display: flex; align-items: center;
  padding: 8px 16px; color: var(--brass-l); font-weight: 700; font-size: 17px; letter-spacing: 0.5px;
  text-shadow: 0 1px 0 hsl(0, 0%, 0%); white-space: nowrap; }
/* settings gear — transparent icon button with a groove, exactly like the game bar's mute/settings buttons */
.ar-camp-top .ar-camp-set { align-self: stretch; background: transparent; border: none;
  border-left: 1px solid hsla(0, 0%, 100%, 0.08); color: hsl(43, 100%, 84.5%); cursor: pointer;
  padding: 0 16px; transition: filter 0.1s, background 0.1s; display: inline-flex; align-items: center; justify-content: center; }
.ar-camp-top .ar-camp-set:hover { filter: brightness(1.18); background: hsla(0, 0%, 100%, 0.1); }
.ar-camp-top .ar-camp-set svg { width: 23px; height: 23px; display: block; }
/* info bar directly under the header — a recessed navy strip like the game's
   prediction line (.ar-pred): fixed-height, its text centred. */
.ar-camp-info { flex: 0 0 auto; line-height: 1.5; font-style: italic;
  background: linear-gradient(180deg, hsl(218, 50.6%, 15.9%) 0%, hsl(219, 58.3%, 9.4%) 55%, hsl(217, 60%, 5.9%) 100%);
  border-left: 2px solid var(--ink); border-right: 2px solid var(--ink); border-bottom: 2px solid var(--brass-d);
  box-shadow: inset 0 5px 7px -5px hsla(0, 0%, 0%, 0.7), inset 0 -1px 0 hsla(43, 63.2%, 58.4%, 0.22);
  color: hsl(212, 62.7%, 88.4%); font-size: 12px; text-align: center; padding: 5px 14px; letter-spacing: 0.3px; }
.ar-camp-info .ar-camp-done { color: hsl(131, 76.4%, 75.1%); font-style: normal; font-weight: 700; }
/* map area: the chart is a FIXED size — it never scales with the viewport. It's
   centred when there's room and the area SCROLLS (both axes) when the viewport is
   smaller. (margin:auto in a flex box centres yet still scrolls from the top-left
   when the map overflows, unlike justify/align-center.) */
.ar-camp-mapwrap { flex: 1 1 auto; min-height: 0; position: relative; overflow: auto; display: flex;
  border-left: 2px solid var(--ink); border-right: 2px solid var(--ink); border-bottom: 2px solid var(--ink);
  background-color: hsl(218, 67.1%, 16.7%);
  /* sea gradient + brick grid across the WHOLE area (not just the fixed chart) */
  background-image:
    repeating-linear-gradient(0deg, hsla(0, 0%, 100%, 0.05) 0 1px, transparent 1px 14px),
    repeating-linear-gradient(90deg, hsla(0, 0%, 100%, 0.04) 0 1px, transparent 1px 40px),
    linear-gradient(180deg, hsl(216, 61.1%, 28.2%), hsl(218, 67.1%, 16.7%)); }
.ar-camp-map { flex: 0 0 auto; margin: auto; width: 960px; height: 520px; }
.ar-camp-isle { cursor: default; }
.ar-camp-isle.playable { cursor: pointer; }
.ar-camp-isle.playable:hover .ar-isle-body { filter: brightness(1.12); }
.ar-camp-isle.playable:hover .ar-isle-ring { opacity: 1; }
.ar-btn { font-family: inherit; font-weight: 700; font-size: 14px; padding: 8px 14px; cursor: pointer; color: var(--txt);
  background: linear-gradient(180deg, var(--brass-l), var(--brass) 55%, var(--brass-d));
  border: 2px solid var(--wood-d); border-radius: 4px;
  box-shadow: inset 0 1px 0 hsla(0, 0%, 100%, 0.55), inset 0 -2px 2px hsla(0, 0%, 0%, 0.18), 0 1px 2px hsla(0, 0%, 0%, 0.3);
  text-shadow: 0 1px 0 hsla(0, 0%, 100%, 0.35); }
.ar-btn:hover { filter: brightness(1.06); }
.ar-btn.primary { color: hsl(90, 100%, 95.7%); background: linear-gradient(180deg, hsl(105, 47.8%, 64.7%), hsl(105, 38.4%, 43.9%) 55%, hsl(105, 41.7%, 29.6%)); border-color: hsl(110, 40.6%, 25.1%); text-shadow: 0 1px 0 hsla(0, 0%, 0%, 0.3); }
.ar-btn.ghost { background: linear-gradient(180deg, hsl(44, 58.4%, 84.9%), hsl(40, 46.9%, 71.2%)); }
.ar-camp-modal { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; z-index: 20;
  background: radial-gradient(ellipse at center, hsla(219, 51.8%, 16.3%, 0.78), hsla(220, 60%, 3.9%, 0.9)); }
.ar-camp-card { width: min(440px, calc(100% - 24px)); background: linear-gradient(180deg, hsl(45, 67.3%, 90.4%), var(--parch) 40%, var(--parch2));
  border: 2px solid var(--wood); border-top: 4px solid var(--brass); border-radius: 6px; padding: 16px 20px;
  box-shadow: 0 8px 30px hsla(0, 0%, 0%, 0.5); }
.ar-camp-card h2 { margin: 0 0 2px; color: var(--head); font-size: 21px; }
.ar-camp-card .region { color: hsl(31, 47.6%, 28.4%); font-style: italic; font-size: 13px; margin-bottom: 10px; }
.ar-camp-card table { width: 100%; border-collapse: collapse; font-size: 14px; margin-bottom: 8px; }
.ar-camp-card td { padding: 4px 2px; border-bottom: 1px solid hsla(31, 47.6%, 28.4%, 0.22); }
.ar-camp-card td.k { color: hsl(31, 47.6%, 28.4%); } .ar-camp-card td.v { text-align: right; font-weight: 700; color: var(--txt); }
.ar-camp-card .reward { background: hsla(43, 63.2%, 58.4%, 0.22); border: 1px solid var(--brass-d); border-radius: 4px;
  padding: 7px 10px; margin: 8px 0 4px; font-size: 13px; }
.ar-camp-card .reward b { color: var(--head); }
.ar-camp-card .note { color: var(--redx); font-size: 12.5px; font-style: italic; margin: 4px 0 8px; }
.ar-camp-card .actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 10px; }
.ar-camp-done { color: var(--ok); font-weight: 700; }

/* header responsive: under 380px the screen name is dropped (dial · points · gear
   stay one row); below that, items just wrap naturally when they don't fit. */
@media (max-width: 379.98px) {
  .ar-camp-top .title { display: none; }
  .ar-camp-top .ar-camp-set { margin-left: auto; }
}
`;

/** Geographic layout of each state island, in the 960×520 map viewBox. */
const ISLE_POS: [number, number][][] = [
  [[150, 150], [280, 250], [140, 370]], // Coastal Provinces (left)
  [[440, 140], [540, 285], [430, 405]], // The Middle Seas (centre)
  [[720, 150], [825, 285], [705, 390]], // Admiral's Reach (right)
];
const REGION_LABEL: [number, number][] = [[150, 55], [490, 55], [765, 55]];
const ISLE_R = 60;
/** fixed organic blob (10 vertices) so islands look hand-drawn, not circular */
const BLOB = [1.0, 0.84, 1.06, 0.9, 1.12, 0.86, 1.04, 0.92, 1.1, 0.82];

function blobPath(cx: number, cy: number, rad: number): string {
  const pts = BLOB.map((m, i) => {
    const a = (i / BLOB.length) * Math.PI * 2 - Math.PI / 2;
    return `${(cx + Math.cos(a) * rad * m).toFixed(1)},${(cy + Math.sin(a) * rad * m).toFixed(1)}`;
  });
  return `M${pts.join('L')}Z`;
}

export interface CampaignMapOpts {
  container: HTMLElement;
  progress: Campaign;
  /** begin the chosen state's battle */
  onStart: (region: number, state: number) => void;
  /** reset all progress (a fresh campaign) */
  onReset?: () => void;
  /** leave the campaign (e.g. to a quick battle) */
  onExit?: () => void;
}

export interface CampaignMapHandle {
  progress(): Campaign;
  swayed(): number;
  complete(): boolean;
  /** programmatically begin a state's battle (used by the E2E) */
  startState(region: number, state: number): void;
  destroy(): void;
}

export function showCampaignMap(opts: CampaignMapOpts): CampaignMapHandle {
  const { container, progress } = opts;
  if (!document.getElementById('ar-campaign-style')) {
    const style = document.createElement('style');
    style.id = 'ar-campaign-style';
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  const wrap = document.createElement('div');
  wrap.className = 'ar-campaign';
  wrap.id = 'ar-campaign';

  const done = swayedCount(progress);
  const complete = campaignComplete(progress);
  const budget = renownBudget(progress.points);
  const swayPct = Math.round((done / TOTAL_STATES) * 100); // fills the "Swayed" dial

  wrap.innerHTML = `
    <div class="ar-camp-top">
      <div class="ar-turn" title="States swayed to the Admiral, of ${TOTAL_STATES}">
        <div class="ar-turn-pie"><i class="ar-turn-pie-fill" style="--swp:${swayPct}"></i></div>
        <div class="ar-turn-text">
          <span class="ar-turn-cap">Swayed</span>
          <span class="ar-turn-count"><b>${done}</b><span class="ar-turn-max">/ ${TOTAL_STATES}</span></span>
        </div>
      </div>
      <div class="stat" title="Your points — the fleet budget your renown has earned for the next battle"><span class="val">${ICON_VP}${budget}</span></div>
      <div class="title">Campaign</div>
      <button class="ar-camp-set" id="ar-camp-settings" title="Settings" aria-label="Settings">${ICON_GEAR}</button>
    </div>
    <div class="ar-camp-info">${complete
      ? '<span class="ar-camp-done">Every state is swayed — the Admiral returns to lead the fleet. Well sailed.</span>'
      : 'Pick a state island to sway it. Win to lower its neighbours’ resistance; reach 0 and they join without a fight.'}</div>
    <div class="ar-camp-mapwrap">${mapSvg(progress)}</div>`;
  container.appendChild(wrap);

  const playable = (r: number, s: number): boolean =>
    regionUnlocked(progress, r) && !isSwayed(progress, r, s);

  // wire island clicks
  wrap.querySelectorAll<SVGGElement>('.ar-camp-isle').forEach((g) => {
    const r = Number(g.dataset['region']);
    const s = Number(g.dataset['state']);
    if (!playable(r, s)) return;
    g.classList.add('playable');
    g.addEventListener('click', () => openCard(r, s));
  });

  // ---- settings overlay (gear button) ----
  // The same panel the game uses (same design + functions), with the campaign's
  // "Return to Main Menu" and "Reset Progress" actions appended in its footer.
  let settingsHandle: SettingsHandle | null = null;
  const closeSettings = (): void => { settingsHandle?.destroy(); settingsHandle = null; };
  const openSettings = (): void => {
    if (settingsHandle) return;
    settingsHandle = showSettings({
      container: wrap,
      overlay: true,
      backLabel: 'Close',
      onBack: closeSettings,
      ...(opts.onExit ? { onReturnToMenu: () => { closeSettings(); opts.onExit!(); } } : {}),
      ...(opts.onReset ? { onResetProgress: () => { closeSettings(); opts.onReset!(); } } : {}),
    });
  };
  wrap.querySelector('#ar-camp-settings')?.addEventListener('click', openSettings);

  function openCard(region: number, state: number): void {
    const def = CAMP_REGIONS[region]!.states[state]!;
    const cfg = battleConfig(progress, region, state);
    const against = isAgainst(progress, region, state);
    const modal = document.createElement('div');
    modal.className = 'ar-camp-modal';
    modal.id = 'ar-camp-modal';
    modal.innerHTML = `
      <div class="ar-camp-card">
        <h2>${def.name}</h2>
        <div class="region">${CAMP_REGIONS[region]!.name} · ${cfg.size} map · ${cfg.aspects} resistance aspect${cfg.aspects === 1 ? '' : 's'}</div>
        <table>
          <tr><td class="k">Resistance</td><td class="v">${stateResistance(progress, region, state)}${against ? ' — swayed against' : ''}</td></tr>
          <tr><td class="k">You must out-score the enemy by</td><td class="v">${cfg.ratio}%</td></tr>
          <tr><td class="k">Your fleet budget</td><td class="v">${ICON_VP}${cfg.budgetR}</td></tr>
          <tr><td class="k">Enemy fleet budget</td><td class="v">${ICON_VP}${cfg.budgetY}</td></tr>
        </table>
        <div class="reward">Reward when swayed: <b>${def.reward}</b></div>
        ${against ? '<div class="note">Swayed against the Admiral — a win only softens its resistance (−2), it won’t sway yet.</div>' : ''}
        <div class="actions">
          <button class="ar-btn ghost" id="ar-camp-cancel">Back</button>
          <button class="ar-btn primary" id="ar-camp-begin">Begin battle ▶</button>
        </div>
      </div>`;
    const close = (): void => { modal.remove(); document.removeEventListener('keydown', onKey); };
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    wrap.appendChild(modal);
    // dismiss by clicking the dark backdrop (outside the card) or pressing Escape
    modal.addEventListener('click', (e) => { if (e.target === modal) close(); });
    modal.querySelector('#ar-camp-cancel')?.addEventListener('click', close);
    modal.querySelector('#ar-camp-begin')?.addEventListener('click', () => { close(); opts.onStart(region, state); });
  }

  return {
    progress: () => progress,
    swayed: () => swayedCount(progress),
    complete: () => campaignComplete(progress),
    startState: (region, state) => opts.onStart(region, state),
    destroy: () => { closeSettings(); wrap.remove(); },
  };
}

/** Build the inline-SVG region chart for the given progress. */
function mapSvg(camp: Campaign): string {
  let isles = '';
  let lanes = '';
  for (let r = 0; r < CAMP_REGIONS.length; r++) {
    const pos = ISLE_POS[r]!;
    const unlocked = regionUnlocked(camp, r);
    // dotted shipping lanes between the region's mutually-adjacent states
    const lanePairs: [number, number][] = [[0, 1], [1, 2], [0, 2]];
    for (const [a, b] of lanePairs) {
      lanes += `<line x1="${pos[a]![0]}" y1="${pos[a]![1]}" x2="${pos[b]![0]}" y2="${pos[b]![1]}"
        stroke="${unlocked ? 'hsla(45, 100%, 90.6%, 0.45)' : 'hsla(0, 0%, 100%, 0.12)'}" stroke-width="2" stroke-dasharray="3 6"/>`;
    }
    // region label
    const [lx, ly] = REGION_LABEL[r]!;
    isles += `<text x="${lx}" y="${ly}" class="rlabel" text-anchor="middle"
      fill="${unlocked ? 'hsl(43, 100%, 84.5%)' : 'hsl(218, 15.6%, 55.9%)'}" font-family="Georgia, serif" font-size="15" font-weight="700"
      style="letter-spacing:0.5px">${CAMP_REGIONS[r]!.name}${unlocked ? '' : ' — locked'}</text>`;
    for (let s = 0; s < STATES_PER_REGION; s++) {
      isles += isleSvg(camp, r, s, pos[s]![0], pos[s]![1], unlocked);
    }
  }
  // The sea gradient + grid live on .ar-camp-mapwrap (CSS) so they cover the whole
  // area, not just the fixed-size chart; the SVG itself is transparent.
  return `<svg class="ar-camp-map" viewBox="0 0 960 520" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Campaign region map">
    ${lanes}
    ${isles}
  </svg>`;
}

function isleSvg(
  camp: Campaign, r: number, s: number, cx: number, cy: number, unlocked: boolean,
): string {
  const def = CAMP_REGIONS[r]!.states[s]!;
  const swayed = isSwayed(camp, r, s);
  const against = isAgainst(camp, r, s);
  const res = stateResistance(camp, r, s);

  let fill = 'hsl(42, 40.1%, 67.3%)';
  let stroke = 'hsl(31, 47.6%, 28.4%)';
  if (!unlocked) {
    fill = 'hsl(84, 4%, 49%)';
    stroke = 'hsl(86, 4.6%, 29.6%)';
  } else if (swayed) {
    fill = 'hsl(95, 31%, 50.6%)';
    stroke = 'hsl(105, 41.7%, 29.6%)';
  } else if (against) {
    fill = 'hsl(11, 40.5%, 56.5%)';
    stroke = 'hsl(9, 56.4%, 30.6%)';
  }

  // status badge text under the initials
  let badge = '';
  if (!unlocked) badge = 'locked';
  else if (swayed) badge = '++';
  else if (against) badge = `✗ res ${res}`;
  else badge = `res ${res}`;
  const badgeColor = swayed ? 'hsl(109, 62.1%, 18.6%)' : against ? 'hsl(8, 70.9%, 21.6%)' : !unlocked ? 'hsl(86, 6.1%, 22.5%)' : 'hsl(30, 54.2%, 18.8%)';

  const ring = `<path class="ar-isle-ring" d="${blobPath(cx, cy, ISLE_R + 7)}" fill="none"
    stroke="hsl(43, 100%, 84.5%)" stroke-width="3" opacity="0" />`;
  const coast = `<path d="${blobPath(cx, cy, ISLE_R + 4)}" fill="hsl(44, 52.1%, 77.1%)" opacity="0.5"/>`;
  const body = `<path class="ar-isle-body" d="${blobPath(cx, cy, ISLE_R)}" fill="${fill}" stroke="${stroke}" stroke-width="2.5"/>`;

  const swayedMark = swayed
    ? `<circle cx="${cx + ISLE_R * 0.55}" cy="${cy - ISLE_R * 0.55}" r="14" fill="hsl(124, 44.4%, 33.1%)" stroke="hsl(0, 0%, 100%)" stroke-width="2"/>
       <text x="${cx + ISLE_R * 0.55}" y="${cy - ISLE_R * 0.55 + 5}" text-anchor="middle" font-size="15" font-weight="700" fill="hsl(0, 0%, 100%)">✓</text>`
    : '';
  const againstMark = against && unlocked
    ? `<text x="${cx}" y="${cy + 4}" text-anchor="middle" font-size="74" font-weight="900" fill="hsla(6, 77.8%, 26.5%, 0.32)">✗</text>`
    : '';

  return `<g class="ar-camp-isle" data-region="${r}" data-state="${s}">
    ${ring}${coast}${body}${againstMark}
    <text x="${cx}" y="${cy - 4}" text-anchor="middle" font-family="Georgia, serif" font-size="26" font-weight="700"
      fill="${unlocked ? 'hsl(28, 55.6%, 10.6%)' : 'hsl(86, 4.6%, 29.6%)'}" style="letter-spacing:1px">${def.code}</text>
    <text x="${cx}" y="${cy + 18}" text-anchor="middle" font-family="Georgia, serif" font-size="13" font-weight="700"
      fill="${badgeColor}">${badge}</text>
    ${swayedMark}
  </g>`;
}
